
import Vue from 'vue'
import {Component, Prop} from 'vue-property-decorator'
import {namespace} from 'vuex-class'
import {BModal} from 'bootstrap-vue'
import UiIcon from '@/components/common/UiIcon.vue'

const groupeModule = namespace('groupe')
type GroupId = number | string
interface Group {
  ID: GroupId
  NOM: string
}

@Component({components: {BModal, UiIcon}})
export default class GroupManager extends Vue {
  @groupeModule.Getter('groupesGetter') groupes: Group[] | null
  @groupeModule.Action('getGroupesAction') getGroupes: any
  @Prop({type: Array, default: () => []}) data!: Group[]
  @Prop({type: Function, required: true}) saveGroups!: (ids: GroupId[]) => Promise<any>

  dialogOpen = false
  search = ''
  selectedOnly = false
  draftIds: GroupId[] = []
  loadedGroups: Group[] = []
  loadingGroups = false
  isSaving = false
  loadError = ''
  saveError = ''

  get assignedGroups() {
    return this.data || []
  }

  get availableGroups() {
    const directory = this.groupes || this.loadedGroups
    const groups = new Map<string, Group>()
    ;[...directory, ...this.assignedGroups].forEach((group) => groups.set(String(group.ID), group))
    return Array.from(groups.values()).sort((a, b) => a.NOM.localeCompare(b.NOM, 'fr', {sensitivity: 'base'}))
  }

  get filteredGroups() {
    const query = this.normalize(this.search.trim())
    return this.availableGroups.filter(
      (group) => (!this.selectedOnly || this.isSelected(group.ID)) && this.normalize(group.NOM).includes(query)
    )
  }

  get hasChanges() {
    const assigned = this.assignedGroups.map((group) => String(group.ID))
    return this.draftIds.length !== assigned.length || this.draftIds.some((id) => !assigned.includes(String(id)))
  }

  normalize(value: string) {
    return value
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase('fr')
  }

  isSelected(id: GroupId) {
    return this.draftIds.some((selected) => String(selected) === String(id))
  }

  toggleGroup(id: GroupId) {
    if (this.isSaving) return
    this.draftIds = this.isSelected(id)
      ? this.draftIds.filter((selected) => String(selected) !== String(id))
      : [...this.draftIds, id]
    this.saveError = ''
  }

  openManager(selectedOnly = false) {
    this.draftIds = this.assignedGroups.map((group) => group.ID)
    this.search = ''
    this.selectedOnly = selectedOnly
    this.saveError = ''
    this.dialogOpen = true
    if (!this.groupes) this.loadGroups()
  }

  async loadGroups() {
    if (this.loadingGroups) return
    this.loadingGroups = true
    this.loadError = ''
    try {
      this.loadedGroups = await this.getGroupes()
    } catch (error) {
      this.loadError = 'Impossible de charger les groupes. Réessayez dans un instant.'
    } finally {
      this.loadingGroups = false
    }
  }

  onHide(event: {preventDefault: () => void}) {
    if (this.isSaving) event.preventDefault()
  }

  async save() {
    if (this.isSaving || this.loadingGroups || this.loadError || !this.hasChanges) return
    this.isSaving = true
    this.saveError = ''
    try {
      await this.saveGroups([...this.draftIds])
      this.isSaving = false
      this.dialogOpen = false
    } catch (error) {
      this.saveError = 'Impossible d’enregistrer les groupes. Vos choix ont été conservés, vous pouvez réessayer.'
    } finally {
      this.isSaving = false
    }
  }
}
